import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineArrowLeft,
  HiOutlineShieldCheck,
  HiOutlineCheck,
  HiOutlineX
} from 'react-icons/hi';
import ThemeToggle from '@/components/common/ThemeToggle';
import authService from '@/services/authService';
import logo from './../assets/logo.svg';

function getServerErrorMessage(err: unknown, fallback: string): string {
  const error = err as any;
  return error?.response?.data?.message || error?.message || fallback;
}

interface PasswordRule {
  key: string;
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { key: 'length', label: 'Al menos 14 caracteres', test: (p) => p.length >= 14 },
  { key: 'uppercase', label: 'Al menos una mayúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'Al menos una minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Al menos un número', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Al menos un carácter especial', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

function usePasswordStrength(password: string) {
  return useMemo(() => {
    const results = PASSWORD_RULES.map((rule) => ({
      ...rule,
      passed: rule.test(password),
    }));
    const passedCount = results.filter((r) => r.passed).length;
    const total = results.length;
    const allPassed = passedCount === total;

    let color = 'var(--text-muted)';
    let label = '';

    if (password.length === 0) {
    } else if (passedCount <= 2) {
      color = '#ef4444';
      label = 'Débil';
    } else if (passedCount <= 3) {
      color = '#f59e0b';
      label = 'Regular';
    } else if (passedCount <= 4) {
      color = '#7ab900';
      label = 'Buena';
    } else {
      color = '#22c55e';
      label = 'Fuerte';
    }

    return { results, passedCount, total, allPassed, color, label };
  }, [password]);
}

function PasswordStrengthIndicator({ password }: { password: string }): JSX.Element | null {
  const { results, passedCount, total, color, label } = usePasswordStrength(password);

  if (password.length === 0) return null;

  return (
    <div className="fade-in" style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: i < passedCount ? color : 'var(--border-color)',
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color }}>
          Seguridad: {label}
        </span>
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {passedCount}/{total} requisitos
        </span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '10px 12px',
        background: 'var(--bg-body, #f9fafb)',
        borderRadius: '8px',
        border: '1px solid var(--border-light, #e5e7eb)',
      }}>
        {results.map((rule) => (
          <div
            key={rule.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.75rem',
              color: rule.passed ? '#22c55e' : 'var(--text-muted)',
              transition: 'color 0.2s ease',
            }}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: rule.passed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(156, 163, 175, 0.12)',
              flexShrink: 0,
              transition: 'background 0.2s ease',
            }}>
              {rule.passed ? (
                <HiOutlineCheck style={{ fontSize: '0.625rem' }} />
              ) : (
                <HiOutlineX style={{ fontSize: '0.625rem' }} />
              )}
            </span>
            {rule.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [isValidating, setIsValidating] = useState<boolean>(true);
  const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
  const [maskedEmail, setMaskedEmail] = useState<string>('');

  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const [touchedNew, setTouchedNew] = useState<boolean>(false);
  const [touchedConfirm, setTouchedConfirm] = useState<boolean>(false);

  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);

  const { allPassed } = usePasswordStrength(newPassword);

  const isNewPasswordEmpty = touchedNew && newPassword.trim() === '';
  const isNewPasswordValid = allPassed;

  const isConfirmEmpty = touchedConfirm && confirmPassword.trim() === '';
  const passwordsMatch = confirmPassword.trim() !== '' && newPassword === confirmPassword && isNewPasswordValid;
  const passwordsMismatch = touchedConfirm && confirmPassword.trim() !== '' && newPassword !== confirmPassword;

  const canSubmit = isNewPasswordValid && passwordsMatch && !isSubmitting;

  useEffect(() => {
    const validateToken = async (): Promise<void> => {
      if (!token) {
        setIsValidating(false);
        setIsTokenValid(false);
        return;
      }

      try {
        const response = await authService.validateResetToken(token);
        const data = response.data;

        if (data && data.valid) {
          setIsTokenValid(true);
          setMaskedEmail(data.email ?? '');
        } else {
          setIsTokenValid(false);
        }
      } catch {
        setIsTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    void validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setTouchedNew(true);
    setTouchedConfirm(true);
    setError('');

    if (!newPassword.trim()) {
      setError('La nueva contraseña es requerida');
      return;
    }

    if (!allPassed) {
      setError('La contraseña no cumple con todos los requisitos de seguridad');
      return;
    }

    if (!confirmPassword.trim()) {
      setError('La confirmación de contraseña es requerida');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.resetPassword({
        token,
        newPassword,
        confirmPassword,
      });
      setResetSuccess(true);
    } catch (err) {
      setError(getServerErrorMessage(err, 'No se pudo restablecer la contraseña. El enlace puede haber expirado.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="login-page">
        <ThemeToggle />
        <div className="login-container fade-in">
          <div className="login-card">
            <div className="login-header text-center">
              <img src={logo} className="img-fluid mb-3" alt="Logo" />
              <div style={{ display: 'flex', justifyContent: 'center', margin: '2rem 0' }}>
                <span className="spinner" />
              </div>
              <p className="login-subtitle">Validando enlace de recuperación...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isTokenValid) {
    return (
      <div className="login-page">
        <ThemeToggle />
        <div className="login-container fade-in">
          <div className="login-card">
            <div className="login-header text-center">
              <img src={logo} className="img-fluid mb-3" alt="Logo" />

              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: '#ef4444',
                  fontSize: '2rem',
                }}
              >
                <HiOutlineExclamationCircle />
              </div>

              <h1 className="login-title">Enlace inválido</h1>
              <p
                className="login-subtitle"
                style={{ maxWidth: '340px', margin: '0.5rem auto 0' }}
              >
                El enlace de recuperación es inválido o ha expirado. Solicita uno nuevo para
                restablecer tu contraseña.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                marginTop: '1.5rem',
              }}
            >
              <Link
                to="/forgot-password"
                className="btn btn-primary btn-lg btn-block"
                style={{ textDecoration: 'none', textAlign: 'center' }}
              >
                Solicitar nuevo enlace
              </Link>

              <Link
                to="/login"
                className="btn btn-block"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <HiOutlineArrowLeft />
                Volver al inicio de sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="login-page">
        <ThemeToggle />
        <div className="login-container fade-in">
          <div className="login-card">
            <div className="login-header text-center">
              <img src={logo} className="img-fluid mb-3" alt="Logo" />

              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'rgba(122, 185, 0, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'var(--secondary)',
                  fontSize: '2rem',
                }}
              >
                <HiOutlineShieldCheck />
              </div>

              <h1 className="login-title">Contraseña restablecida</h1>
              <p
                className="login-subtitle"
                style={{ maxWidth: '340px', margin: '0.5rem auto 0' }}
              >
                Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión con tu
                nueva contraseña.
              </p>
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <button
                type="button"
                className="btn btn-primary btn-lg btn-block"
                onClick={() => navigate('/login')}
              >
                Ir a iniciar sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <ThemeToggle />

      <div className="login-container fade-in">
        <div className="login-card">
          <div className="login-header text-center">
            <img src={logo} className="img-fluid mb-3" alt="Logo" />
            <h1 className="login-title">Nueva contraseña</h1>
            <p className="login-subtitle">
              {maskedEmail
                ? <>Ingresa la nueva contraseña para <strong>{maskedEmail}</strong></>
                : 'Ingresa tu nueva contraseña'}
            </p>
          </div>

          {error && (
            <div
              className="alert alert-danger d-flex align-items-center gap-2 mb-4 fade-in"
              role="alert"
            >
              <HiOutlineExclamationCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }} noValidate>
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                Nueva contraseña
              </label>
              <div className="input-group" style={{ position: 'relative' }}>
                <span className="input-icon">
                  <HiOutlineLockClosed />
                </span>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  className={`form-control has-icon-left has-icon-right ${
                    touchedNew && isNewPasswordValid ? 'is-valid' : ''
                  } ${isNewPasswordEmpty ? 'is-invalid' : ''}`}
                  placeholder="••••••••••••••"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (error) setError('');
                  }}
                  onBlur={() => setTouchedNew(true)}
                  autoComplete="new-password"
                  autoFocus
                  disabled={isSubmitting}
                  style={{ paddingRight: '45px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="password-toggle-btn"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    zIndex: 10,
                  }}
                  aria-label={showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showNewPassword ? <HiOutlineEyeOff size={20} /> : <HiOutlineEye size={20} />}
                </button>
              </div>

              <PasswordStrengthIndicator password={newPassword} />

              {isNewPasswordEmpty && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  La nueva contraseña es requerida
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirmar contraseña
              </label>
              <div className="input-group" style={{ position: 'relative' }}>
                <span className="input-icon">
                  <HiOutlineLockClosed />
                </span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`form-control has-icon-left has-icon-right ${
                    passwordsMatch ? 'is-valid' : ''
                  } ${isConfirmEmpty || passwordsMismatch ? 'is-invalid' : ''}`}
                  placeholder="••••••••••••••"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                  }}
                  onBlur={() => setTouchedConfirm(true)}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  style={{ paddingRight: '45px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="password-toggle-btn"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    zIndex: 10,
                  }}
                  aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <HiOutlineEyeOff size={20} />
                  ) : (
                    <HiOutlineEye size={20} />
                  )}
                </button>
              </div>

              {passwordsMatch && (
                <p className="form-text fade-in" style={{ color: '#22c55e' }}>
                  <HiOutlineCheckCircle style={{ marginRight: 4 }} />
                  Las contraseñas coinciden
                </p>
              )}
              {isConfirmEmpty && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  La confirmación de contraseña es requerida
                </p>
              )}
              {passwordsMismatch && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  Las contraseñas no coinciden
                </p>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={!canSubmit}
              style={{
                marginTop: '0.5rem',
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  <span>Restableciendo...</span>
                </>
              ) : (
                'Restablecer contraseña'
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Link
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--primary)',
                fontSize: '0.875rem',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              <HiOutlineArrowLeft />
              Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;